import { useEffect, useState } from "react";

import { GET_EMPLOYEE_PROFILE_FIELDS } from "../../../../../config/Endpoints.js";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type { EmployeeProfileFields } from "../types.ts";

function stringValue(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim() : "-";
}

function parseProfile(payload: unknown): EmployeeProfileFields | null {
  const source = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  const wrapped = source.data && typeof source.data === "object" ? source.data as Record<string, unknown> : source;
  const item = Array.isArray(wrapped.content) ? wrapped.content[0] : null;
  if (!item || typeof item !== "object") return null;
  const row = item as Record<string, unknown>;
  const basic = (row.basicDetails ?? {}) as Record<string, unknown>;
  const insurance = (row.insuranceDetails ?? basic.insuranceDetails ?? {}) as Record<string, unknown>;
  const work = (row.workDetails ?? {}) as Record<string, unknown>;
  const personal = (row.personalDetails ?? {}) as Record<string, unknown>;
  return {
    employeeId: stringValue(row.employeeId),
    basicDetails: {
      projectEligibility: typeof basic.projectEligibility === "boolean" ? basic.projectEligibility : null,
      emailAddress: stringValue(basic.emailAddress),
      primaryEmail: stringValue(basic.primaryEmail),
      middleName: stringValue(basic.middleName),
      lastName: stringValue(basic.lastName),
      firstName: stringValue(basic.firstName),
      insuranceCardNumber: stringValue(
        basic.insuranceCardNumber ?? basic.insuranceNumber ?? insurance.insuranceNumber ?? wrapped.insuranceNumber,
      ),
      insuranceValidFrom: stringValue(
        basic.insuranceFrom ?? insurance.insuranceFrom ?? row.insuranceFrom ?? wrapped.insuranceFrom,
      ),
      insuranceValidTill: stringValue(
        basic.insuranceTo ?? insurance.insuranceTo ?? row.insuranceTo ?? wrapped.insuranceTo,
      ),
    },
    workDetails: {
      department: stringValue(work.department), dateOfJoining: stringValue(work.dateOfJoining),
      techStack: stringValue(work.techStack), designation: stringValue(work.designation),
      employeeType: stringValue(work.employeeType), reportingManager: stringValue(work.reportingManager),
      employeeSubType: stringValue(work.employeeSubType), leaveManager: stringValue(work.leaveManager),
      currentLocation: stringValue(work.currentLocation), joiningLocation: stringValue(work.joiningLocation),
      location: stringValue(work.location),
      trailhead: stringValue(work.trailhead),
    },
    personalDetails: {
      bloodGroup: stringValue(personal.bloodGroup),
      dateOfBirth: stringValue(personal.dateOfBirth),
    },
  };
}

export function useEmployeeProfileFields(employeeId: string | undefined) {
  const [profile, setProfile] = useState<EmployeeProfileFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeId) { setLoading(false); setError("Employee ID is required."); return; }
    const controller = new AbortController();
    setLoading(true); setError(null);
    void getApi<unknown>(`${GET_EMPLOYEE_PROFILE_FIELDS}?employeeId=${encodeURIComponent(employeeId)}`, accessToken(), "json", controller.signal)
      .then(({ data }) => { const parsed = parseProfile(data); setProfile(parsed); if (!parsed) setError("Employee information is not available."); })
      .catch((cause) => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Failed to load employee information."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [employeeId]);

  return { profile, loading, error };
}
